/**
 * Mapa — origem geográfica dos acessos por fonte (Google Ads, SEO, Meta Ads).
 *
 * Mapa interativo (estados e cidades) com um painel lateral: fonte, filtros
 * (origem, mídia, campanha, página, referer, dispositivo, palavra-chave) e cards
 * de métrica. Os dados vêm de /api/mapa/geo, todos da tabela de acessos.
 */

import { useEffect, useState } from 'react'
import Layout from '../../components/Layout.jsx'
import { PeriodPicker } from '../../components/UI.jsx'
import LeadsMap from '../../components/LeadsMap.jsx'
import MapFilterPanel from '../../components/MapFilterPanel.jsx'
import CityDrawer from '../../components/CityDrawer.jsx'
import CityCompare from '../../components/CityCompare.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import { useMapaGeo } from '../../hooks/useMapaGeo.js'
import { useMapaCities } from '../../hooks/useMapaCities.js'
import { METRIC_LABEL } from '../../lib/metricFilters.js'
import {
  MAPA_DEFAULT_SOURCE,
  MAPA_DEFAULT_METRIC,
  MAPA_EMPTY_FILTERS,
  countActiveFilters,
  MAX_COMPARE_CITIES,
} from '../../lib/mapaFilters.js'

const NO_GEO = []
const PANEL_KEY = 'mapa.panelOpen'

// Painel aberto por padrão (fechado em tela estreita); a escolha fica no navegador.
function initialPanelOpen() {
  try {
    const saved = window.localStorage.getItem(PANEL_KEY)
    if (saved === '1' || saved === '0') return saved === '1'
  } catch { /* sem localStorage: usa o padrão */ }
  return !window.matchMedia?.('(max-width: 1100px)').matches
}

export default function Mapa() {
  const { period, selectedClient } = useApp()
  const [source, setSource] = useState(MAPA_DEFAULT_SOURCE)
  const [metric, setMetric] = useState(MAPA_DEFAULT_METRIC)
  const [filters, setFilters] = useState(MAPA_EMPTY_FILTERS)
  const [cities, setCities] = useState([]) // [{ uf, name }]: 1 = painel da cidade, 2+ = comparação
  const [panelOpen, setPanelOpen] = useState(initialPanelOpen)

  const { data, loading, error, refetch } = useMapaGeo(
    period.start, period.end, selectedClient?.id, source, metric, filters,
  )

  const cityResults = useMapaCities(
    period.start, period.end, selectedClient?.id, source, metric, filters, cities,
  )

  const togglePanel = () =>
    setPanelOpen((open) => {
      try {
        window.localStorage.setItem(PANEL_KEY, open ? '0' : '1')
      } catch { /* ignora */ }
      return !open
    })

  // Esc fecha o painel da cidade (ou da comparação).
  const hasCities = cities.length > 0
  useEffect(() => {
    if (!hasCities) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') setCities([])
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [hasCities])

  // Clicar numa cidade a seleciona; clicar de novo nela a remove. Com 2 a 4 cidades
  // o painel vira uma comparação (a mais antiga sai quando passa do limite).
  const toggleCity = ({ uf, name }) => {
    const exists = cities.some((c) => c.uf === uf && c.name === name)
    if (exists) {
      setCities(cities.filter((c) => !(c.uf === uf && c.name === name)))
      return
    }
    setCities([...cities, { uf, name }].slice(-MAX_COMPARE_CITIES))
    // Em tela larga a cidade abre ao lado dos filtros; em tela estreita não cabe, então os filtros recolhem.
    if (window.matchMedia?.('(max-width: 1100px)').matches) setPanelOpen(false)
  }
  const removeCity = (c) => setCities(cities.filter((x) => !(x.uf === c.uf && x.name === c.name)))

  // Valor clicado no painel da cidade vira filtro do mapa (a origem troca a Fonte).
  const applyFilter = (dimension, value) => {
    if (dimension === 'origem') {
      changeOrigin(value)
    } else {
      setFilters((cur) => ({ ...cur, [dimension]: [value] }))
    }
  }

  // Os valores de cada filtro dependem da fonte: trocar a fonte limpa os filtros.
  const changeSource = (key) => {
    setSource(key)
    setFilters(MAPA_EMPTY_FILTERS)
  }

  // Origem escolhida na lista de fontes: todos os acessos daquela origem (utm_source).
  const changeOrigin = (value) => {
    setSource('all')
    setFilters({ ...MAPA_EMPTY_FILTERS, origem: [value] })
  }

  return (
    <Layout breadcrumb={[{ label: 'Marketing' }, { label: 'Mapa' }]} title="Mapa" showPeriod={false}>
      {error && (
        <div className="dash-error">
          <Icon n="alert" s={20} />
          <p>Erro ao carregar dados: {error}</p>
          <button className="dash-error-retry" onClick={refetch} type="button">
            Tentar novamente
          </button>
        </div>
      )}

      <div className="mapa-grid">
        <div className={`mapa-map${loading ? ' is-loading' : ''}`}>
          <LeadsMap
            data={data?.leadsGeo ?? NO_GEO}
            metricLabel={METRIC_LABEL[metric] || metric}
            meta={data?.leadsGeoMeta ?? null}
            zoomable
            onCityClick={toggleCity}
            selectedCities={cities}
          >
            <div className="mapa-period">
              <PeriodPicker openRight />
            </div>
            <div className={`mapa-right${cities.length > 1 ? ' is-compare' : ''}`}>
            <div className={`mapa-panel${panelOpen ? '' : ' is-collapsed'}`}>
              <button
                type="button"
                className="mapa-panel-toggle"
                aria-expanded={panelOpen}
                aria-controls="mapa-panel-body"
                onClick={togglePanel}
              >
                <Icon n="sliders" s={14} />
                <span className="mapa-panel-toggle-label">Fonte e filtros</span>
                {countActiveFilters(filters) > 0 && <span className="mf-badge">{countActiveFilters(filters)}</span>}
                <Icon n="chevron_d" s={12} />
              </button>
              {panelOpen && (
                <div id="mapa-panel-body" className="mapa-panel-body">
                  <MapFilterPanel
                    source={source}
                    onSourceChange={changeSource}
                    onOriginChange={changeOrigin}
                    filters={filters}
                    onFiltersChange={setFilters}
                    metric={metric}
                    onMetricChange={setMetric}
                    totals={data?.totals}
                    startDate={period.start}
                    endDate={period.end}
                    clientId={selectedClient?.id}
                  />
                </div>
              )}
            </div>
            {cities.length === 1 && (
              <CityDrawer
                city={cities[0]}
                data={cityResults[0]?.data}
                loading={cityResults[0]?.loading}
                error={cityResults[0]?.error}
                metric={metric}
                metricLabel={METRIC_LABEL[metric] || metric}
                onMetricChange={setMetric}
                onApplyFilter={applyFilter}
                onClose={() => setCities([])}
              />
            )}
            {cities.length > 1 && (
              <CityCompare
                cities={cities}
                results={cityResults}
                metric={metric}
                metricLabel={METRIC_LABEL[metric] || metric}
                onMetricChange={setMetric}
                onApplyFilter={applyFilter}
                onRemove={removeCity}
                onClose={() => setCities([])}
              />
            )}
            </div>
          </LeadsMap>
        </div>
      </div>
    </Layout>
  )
}
